/**
 * Tenant classification-dimension reads and writes (WSRADM-0025).
 *
 * Bare `/admin/dimensions/**` paths so `resolveBaseUrl` uses `apiBase()` (`/api`).
 * Tenant is the JWT — never a slug on the path, query, or header. Bearer only;
 * no cookies, no CSRF. Never `/admin/platform/**`. A malformed PUT response
 * throws; the client does not re-GET.
 */
import { request } from '@/api/client';
import {
  parseDimensionBoard,
  parseDimensionBoardSaved,
  parseDimensionStructure,
  parseDimensionStructureSaved,
} from './parse';
import type {
  BoardPutBody,
  DimensionBoard,
  DimensionBoardSaved,
  DimensionStructure,
  DimensionStructureSaved,
  StructurePutBody,
} from './types';

function buildQuery(opts?: {
  build?: boolean;
  includeRetired?: boolean;
}): Record<string, string> | undefined {
  const query: Record<string, string> = {};
  if (opts?.build === false) {
    query.build = 'false';
  }
  if (opts?.includeRetired === true) {
    query.includeRetired = 'true';
  }
  return Object.keys(query).length > 0 ? query : undefined;
}

export function getDimensions(): Promise<DimensionStructure> {
  return request<unknown>('GET', '/admin/dimensions').then(parseDimensionStructure);
}

export function putDimensions(
  body: StructurePutBody,
  opts?: { build?: boolean },
): Promise<DimensionStructureSaved> {
  const query = buildQuery(opts);
  return request<unknown>('PUT', '/admin/dimensions', {
    json: body,
    ...(query !== undefined ? { query } : {}),
  }).then(parseDimensionStructureSaved);
}

export function getDimensionBoard(
  dimension: string,
  opts: { includeRetired: boolean },
): Promise<DimensionBoard> {
  return request<unknown>('GET', `/admin/dimensions/${encodeURIComponent(dimension)}/board`, {
    ...(opts.includeRetired === true ? { query: { includeRetired: 'true' } } : {}),
  }).then(parseDimensionBoard);
}

export function putDimensionBoard(
  dimension: string,
  body: BoardPutBody,
  opts?: { build?: boolean; includeRetired?: boolean },
): Promise<DimensionBoardSaved> {
  const query = buildQuery(opts);
  return request<unknown>('PUT', `/admin/dimensions/${encodeURIComponent(dimension)}/board`, {
    json: body,
    ...(query !== undefined ? { query } : {}),
  }).then(parseDimensionBoardSaved);
}
